import * as types from '../constants/idpAdminActions';
import {authApi, RESULT} from "../api/api";
import {removeSessionToken, setApplianceUrl, setMappedCapabilities, setSessionToken} from "./storage";

export const LAUNCH_ERROR = {
  INVALID_CONTEXT: 'INVALID_CONTEXT',
  SIGN_IN_FAILED: 'SIGN_IN_FAILED',
  CONFIGURATION: 'CONFIGURATION',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  GENERIC: 'GENERIC',
};

const HYBRID_UI_CAPABILITY_GROUP = "HybridUI";
const DECIMAL_ID = /^[1-9][0-9]*$/;

const isNonEmptyString = (value) => typeof value === "string" && value.length > 0;
const isObject = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
const isHybridUiGroup = (group) => group.name === HYBRID_UI_CAPABILITY_GROUP;
// Only HybridUI groups reach the service, so the shape of the other groups is the host's business.
const isCapabilityGroup = (group) => isObject(group) && typeof group.name === "string"
    && (!isHybridUiGroup(group) || (Array.isArray(group.capabilities)
        && group.capabilities.every(capability => isObject(capability) && typeof capability.capabilityName === "string")));

// postMessage throws on a targetOrigin that is not an origin, so applianceUrl must be one exactly.
const isOrigin = (url) => {
  try {
    return new URL(url).origin === url;
  } catch (e) {
    return false;
  }
};

const parseJson = (data) => {
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

export const parseContextPayload = (data) => {
  const payload = typeof data === "string" ? parseJson(data) : null;
  if (!isObject(payload)) {
    return null;
  }
  const {authToken, enterpriseId, applianceUuid, idpInstanceId, capabilities, extendedAttributes, applianceUrl} = payload;
  // enterpriseId is required by the contract but unused here, so an empty value must not block the launch.
  const isValid = isNonEmptyString(authToken)
      && typeof enterpriseId === "string"
      && isNonEmptyString(applianceUuid)
      && typeof idpInstanceId === "string" && DECIMAL_ID.test(idpInstanceId)
      && Number.isSafeInteger(Number(idpInstanceId))
      && Array.isArray(capabilities) && capabilities.every(isCapabilityGroup)
      && isObject(extendedAttributes)
      && isNonEmptyString(applianceUrl) && isOrigin(applianceUrl);
  if (!isValid) {
    return null;
  }
  return {
    authToken,
    applianceUuid,
    applianceUrl,
    idpInstanceId: Number(idpInstanceId),
    capabilities: capabilities
    .filter(isHybridUiGroup)
    .reduce((flat, group) => flat.concat(group.capabilities), []),
  };
};

const signInFailure = (status) => {
  switch (status) {
    case 401:
    case 500:
      return LAUNCH_ERROR.SIGN_IN_FAILED;
    case 404:
      return LAUNCH_ERROR.CONFIGURATION;
    default:
      return LAUNCH_ERROR.GENERIC;
  }
};

export const handshakeAccepted = (applianceUrl) => ({
  type: types.HANDSHAKE_ACCEPTED,
  applianceUrl,
});
export const handshakeRejected = () => ({
  type: types.HANDSHAKE_REJECTED
});
export const authStart = () => ({
  type: types.AUTH_START
});
export const authSuccess = (response) => ({
  type: types.AUTH_SUCCESS,
  response,
});
export const authFail = (launchError) => ({
  type: types.AUTH_FAIL,
  launchError,
});
export const sessionExpired = () => ({
  type: types.SESSION_EXPIRED
});

export const mapCapabilities = (capabilities) =>
    Object.assign({}, ...capabilities.map(capability => ({[capability.capabilityName]: true})));

export function signIn(payload) {
  return dispatch => {
    setApplianceUrl(payload.applianceUrl);
    setMappedCapabilities(JSON.stringify(mapCapabilities(payload.capabilities)));
    dispatch(handshakeAccepted(payload.applianceUrl));
    dispatch(authStart());
    return authApi(payload.authToken, payload.idpInstanceId, payload.applianceUuid, payload.capabilities)
    .then(result => {
      if (result.kind === RESULT.OK) {
        setSessionToken(result.body.authSessionToken);
        dispatch(authSuccess(result.body));
      } else {
        removeSessionToken();
        dispatch(authFail(signInFailure(result.status)));
      }
    });
  }
}

export function postToHost(message) {
  return (dispatch, getState) => {
    const {applianceUrl} = getState().auth;
    if (applianceUrl) {
      window.parent.postMessage(message, applianceUrl);
    }
  }
}
